import { useEffect, useState } from "react";
import useVideoMetadata from "../../components/useVideoMetadata";
import {
  editSourceUrl,
  loadStartTemplate,
  pickStartTemplate,
  resolveDeliveryUrl,
  savedTemplateIdOf,
} from "../../components/vreEditSession";

const LOADING = { status: "loading", template: null, notice: "" };

/**
 * The template one batch tab opens on — the same rule as the single editor:
 * a template picked in this session's gallery, else the one the saved edit was
 * made on, else the brand's (horizontal for a landscape video when assigned,
 * else vertical). `status: "gallery"` means the tab has to choose one, with
 * `notice` saying why when a template exists but could not be opened.
 */
export default function useTabTemplate({ item, chosen, vertical, horizontal, templatesLoading, fetchTemplate }) {
  const [state, setState] = useState(LOADING);
  const savedPayload = item.config?.renderPayload;
  const savedId = savedTemplateIdOf(savedPayload);

  // The frame decides between the brand's two templates, so it is only read
  // when there is a horizontal one to choose.
  const needShape = !chosen && !savedId && Boolean(horizontal);
  const shape = useVideoMetadata(resolveDeliveryUrl(editSourceUrl(item.video), "s3"), needShape);
  const shapeKnown = !needShape || shape.status === "ready" || shape.status === "error";
  const landscape = shape.status === "ready" && shape.width > shape.height;
  const verticalId = vertical?.id ?? null;
  const horizontalId = horizontal?.id ?? null;

  useEffect(() => {
    if (chosen) {
      setState({ status: "ready", template: chosen, notice: "" });
      return undefined;
    }
    if (templatesLoading || !shapeKnown) {
      setState(LOADING);
      return undefined;
    }
    const pre = pickStartTemplate({ savedPayload, landscape, vertical, horizontal });
    if (!pre) {
      setState({ status: "gallery", template: null, notice: "" });
      return undefined;
    }
    // Saving this tab points its saved edit at the template already open;
    // reloading it would restart the editor for nothing.
    if (state.template && String(state.template.id) === String(pre.id ?? "")) return undefined;
    let live = true;
    setState(LOADING);
    loadStartTemplate(pre, fetchTemplate).then(({ template, notice }) => {
      if (!live) return;
      setState(template ? { status: "ready", template, notice: "" } : { status: "gallery", template: null, notice });
    });
    return () => {
      live = false;
    };
    // Objects are rebuilt every render; their ids decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, templatesLoading, shapeKnown, landscape, savedId, verticalId, horizontalId, fetchTemplate]);

  return state;
}
